import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Create from "../components/Testimonials/Create";
import Edit from "../components/Testimonials/Edit";
import api from "../api/api";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Alerts from "../components/Alerts";

const Testimonials = () => {
  const [step, setStep] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [idToEdit, setIdToEdit] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [warning, setWarning] = useState({});

  useEffect(() => {
    if (step !== "") return;

    const fetchTestimonials = async () => {
      setIsLoading(true);
      try {
        const res = await api.get("testimonials");
        if (res.status === 200) {
          // Ordenar por order (crescente) e, em caso de empate, por createdAt (descendente)
          const sortedTestimonials = res.data.testimonials.sort((a, b) => {
            // Primeiro, ordenar por order (crescente)
            if (a.order !== b.order) {
              return (a.order || 0) - (b.order || 0);
            }
            // Se order for igual, ordenar por createdAt (descendente - mais recentes primeiro)
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
          });
          setTestimonials(sortedTestimonials);
        } else {
          setTestimonials([]);
          console.error("Erro ao carregar os depoimentos.");
        }
      } catch (error) {
        console.error("Erro ao buscar depoimentos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, [step]);

  const handleCreateClick = () => setStep("create");
  const handleEditClick = (id) => {
    setIdToEdit(id);
    setStep("edit");
  };

  const handleEditSuccess = () => {
    setWarning({
      type: "success",
      message: "Depoimento editado com sucesso.",
    });
    handleOpenSnackbar();
  };

  const handleDeleteClick = (id) => {
    if (window.confirm("Deseja realmente deletar este depoimento?")) {
      api.delete(`testimonials/${id}`).then((res) => {
        if (res.status === 200) {
          setTestimonials(
            testimonials.filter((testimonial) => testimonial.id !== id)
          );
          setWarning({
            type: "success",
            message: "Depoimento deletado com sucesso.",
          });
          handleOpenSnackbar();
        }
      });
    }
  };

  const handleOpenSnackbar = () => {
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-lg">
      <Header category="Home Page" title="Depoimentos" />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <CircularProgress size={60} />
          <p className="mt-4 text-gray-600">Carregando depoimentos...</p>
        </div>
      ) : step === "create" ? (
        <Create setStep={setStep} />
      ) : step === "edit" ? (
        <Edit
          setStep={setStep}
          testimonialId={idToEdit}
          onSuccess={handleEditSuccess}
        />
      ) : step === "" && testimonials.length > 0 ? (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Gerenciar Depoimentos
              </h1>
              <p className="text-gray-600">
                Total de depoimentos:{" "}
                <span className="font-semibold text-blue-600">
                  {testimonials.length}
                </span>
              </p>
            </div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateClick}
              className="px-6 py-3"
            >
              <div className="flex items-center gap-2">
                <AddIcon />
                Criar Novo Depoimento
              </div>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:border-blue-300 p-4"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-800 line-clamp-2 flex-1 mr-3">
                    {testimonial.name}
                  </h3>
                  <Tooltip title="Editar">
                    <IconButton
                      color="primary"
                      onClick={() => handleEditClick(testimonial.id)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Deletar">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(testimonial.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>

                {/* video aqui */}
                <div className="space-y-4 mt-4 flex flex-col justify-center items-center h-[400px]">
                  {testimonial.videoUrl && (
                    <Box
                      component="iframe"
                      src={`https://player.vimeo.com/video/${testimonial.videoUrl}`}
                      title={`Depoimento de ${testimonial.name}`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  )}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="text-sm mb-2">
                      <span className="text-gray-600">Ordem: </span>
                      <span className="font-semibold text-yellow-600">
                        {testimonial.order}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Avaliação: </span>
                      <span className="font-semibold text-yellow-600">
                        {testimonial.starRating} estrelas
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-lg mb-4">
            Nenhum depoimento encontrado
          </p>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateClick}
            className="px-6 py-3"
          >
            <div className="flex items-center gap-2">
              <AddIcon />
              Criar Primeiro Depoimento
            </div>
          </Button>
        </div>
      )}
      <Alerts
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        message={warning.message}
        type={warning.type}
      />
    </div>
  );
};

export default Testimonials;
